import { useEffect, useRef } from 'react';

import { cn } from '@crossnokaye/ux-common';

export interface ColumnOverlayProperties {
  /** Optional color for the overlay border/indicator */
  color?: string;
  /** Message to display to the user */
  message: string;
}

interface ColumnOverlayProps {
  /** Index of the column */
  columnIndex: number;
  /** Overlay properties for validation */
  overlayProperty?: ColumnOverlayProperties;
  /** Reference to the table header cell for positioning */
  targetElement: HTMLTableCellElement | null;
}

/**
 * ColumnOverlay displays validation feedback on sequencer table columns.
 * It renders a border around the column and displays a message below it.
 */
export const ColumnOverlay = ({
  columnIndex,
  overlayProperty,
  targetElement,
}: ColumnOverlayProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Update overlay position and size when target element changes or columns are added/removed
  useEffect(() => {
    if (overlayProperty && targetElement && overlayRef.current) {
      const overlay = overlayRef.current;
      const table = targetElement.closest('table');
      if (table) {
        overlay.style.left = `${targetElement.offsetLeft - 1}px`;
        overlay.style.width = `${targetElement.offsetWidth + 1}px`;
        overlay.style.height = `${table.offsetHeight}px`;
      }
    }
  }, [targetElement, overlayProperty]);

  if (!overlayProperty) {
    return null;
  }

  return (
    <div
      className={cn('absolute pointer-events-none rounded-sm top-0 ring-1 ring-warning ring-inset')}
      data-column-overlay={columnIndex}
      ref={overlayRef}
    >
      <div className="relative w-full h-full">
        <p
          className={cn(
            'absolute w-full pointer-events-none',
            'top-[100%] mt-1',
            'text-center text-xs font-semibold whitespace-nowrap text-ckds-base-caution'
          )}
        >
          {overlayProperty.message}
        </p>
      </div>
    </div>
  );
};
